import React from "react";
import { Check, X } from "lucide-react";

const TOC = [
  "Objectives", "Scope", "Solution Overview", "Process Flow — Stock Issuance",
  "Process Flow — Stock Distribution to Region",
  "Process Flow — Replenishment", "Process Flow — Order to Install",
  "Process Flow — Returns", "Process Flow — Reporting", "Roles & Permissions Matrix",
  "Integration Summary", "Business Rules & Integration Data", "Key Performance Indicators", "Open Information Requirements",
];

const Page = ({ id, children, style }) => (
  <section id={id} className="doc-page" style={style}>{children}</section>
);

const SectionHead = ({ num, title, primary, navy }) => (
  <div className="border-b-2 pb-2 mb-5" style={{ borderColor: primary }}>
    <h2 className="font-head text-3xl font-bold" style={{ color: navy }}>
      <span className="font-label text-lg mr-3" style={{ color: primary }}>{num}</span>
      {title}
    </h2>
  </div>
);

export const DocumentPreview = ({ content, branding, diagrams }) => {
  const navy = branding.secondary_color;
  const teal = branding.primary_color;
  const logo = branding.logo_text || "NETSTAR";
  const pad = "px-14 py-12";

  return (
    <div className="flex flex-col items-center">
      {/* COVER */}
      <Page id="sec-cover" style={{ background: navy, color: "#fff", minHeight: 1123 }}>
        <div className="h-full flex flex-col justify-between p-14" style={{ minHeight: 1123 }}>
          <div>
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="logo" className="h-12" />
            ) : (
              <div className="font-head text-4xl font-bold tracking-widest">
                {logo.slice(0, -1)}<span style={{ color: teal }}>{logo.slice(-1)}</span>
              </div>
            )}
            <div className="mt-4 h-1.5 w-24" style={{ background: teal }} />
          </div>
          <div>
            <div className="overline" style={{ color: teal }}>Solution Proposal</div>
            <h1 className="font-head text-5xl font-bold leading-tight mt-3 max-w-[560px]">{branding.document_title}</h1>
            <div className="font-head text-xl font-semibold mt-3" style={{ color: teal }}>{branding.document_subtitle}</div>
            <img src={branding.cover_image} alt="cover" className="w-full mt-8 rounded-sm object-cover" style={{ height: 300 }} />
          </div>
          <div className="text-sm text-slate-300 space-y-3 mt-6">
            <div><div className="text-white font-head text-base font-semibold">{branding.author}</div>{branding.author_role}</div>
            <div>{branding.footer_text}</div>
          </div>
        </div>
      </Page>

      {/* CONTENTS */}
      <Page id="sec-contents" style={{ minHeight: 1123 }}>
        <div className={pad}>
          <SectionHead num="01" title="Contents" primary={teal} navy={navy} />
          {TOC.map((t, i) => (
            <div key={t} className="flex justify-between py-2.5 border-b border-slate-200 text-[15px]">
              <span className="font-head font-semibold" style={{ color: navy }}>{t}</span>
              <span className="font-label font-bold" style={{ color: teal }}>{String(i + 2).padStart(2, "0")}</span>
            </div>
          ))}
        </div>
      </Page>

      {/* OBJECTIVES */}
      <Page id="sec-objectives" style={{ minHeight: 1123 }}>
        <div className={pad}>
          <SectionHead num="02" title="Objectives" primary={teal} navy={navy} />
          <p className="text-[15px] text-slate-700 mb-5">The following objectives define the scope and intent of the Consignment Stock Management Platform pilot. Each is measurable and maps directly to the operational flows and KPIs set out later in this document.</p>
          {content.objectives.map((o) => (
            <div key={o.title} className="pl-4 mb-4" style={{ borderLeft: `3px solid ${teal}` }}>
              <h3 className="font-head font-semibold text-[15px]" style={{ color: navy }}>{o.title}</h3>
              <p className="text-sm text-slate-600">{o.description}</p>
            </div>
          ))}
        </div>
      </Page>

      {/* SCOPE */}
      <Page id="sec-scope" style={{ minHeight: 1123 }}>
        <div className={pad}>
          <SectionHead num="03" title="Scope" primary={teal} navy={navy} />
          <p className="text-[15px] text-slate-700 mb-6">This section defines the boundaries of the consignment stock pilot — what the platform delivers, and what remains the responsibility of adjacent systems and teams.</p>
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-1">
              <span className="overline" style={{ color: teal }}>Consumer Product Range — Pilot Scope</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">{content.product_range.note}</p>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>{["Product", "VBU Code(s)", "Category", "Status"].map((h) => (
                  <th key={h} className="text-left p-2.5 text-white font-head" style={{ background: navy }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {content.product_range.items.map((p, idx) => (
                  <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}>
                    <td className="p-2.5 border-b border-slate-200 font-semibold align-top" style={{ color: navy }}>{p.product}</td>
                    <td className="p-2.5 border-b border-slate-200 text-slate-500 align-top font-label">{p.vbu}</td>
                    <td className="p-2.5 border-b border-slate-200 text-slate-600 align-top">{p.category}</td>
                    <td className="p-2.5 border-b border-slate-200 align-top"><span className="px-2 py-0.5 rounded-sm text-[10px] font-bold" style={{ background: `${teal}22`, color: teal }}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="overline mb-4" style={{ color: teal }}>In Scope</div>
              {content.scope.in_scope.map((s, i) => (
                <div key={i} className="flex gap-2.5 mb-3.5">
                  <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: teal }} />
                  <p className="text-sm text-slate-700">{s}</p>
                </div>
              ))}
            </div>
            <div>
              <div className="overline mb-4 text-slate-400">Out of Scope</div>
              {content.scope.out_of_scope.map((s, i) => (
                <div key={i} className="flex gap-2.5 mb-3.5">
                  <X className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
                  <p className="text-sm text-slate-600">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Page>

      {/* OVERVIEW */}
      <Page id="sec-overview" style={{ minHeight: 1123 }}>
        <div className={pad}>
          <SectionHead num="04" title="Solution Overview" primary={teal} navy={navy} />
          <p className="text-[15px] text-slate-700 mb-4">{content.solution_overview.narrative}</p>
          <img src={diagrams.overview} alt="overview" className="w-full border border-slate-200 rounded-sm my-4" />
          <div className="overline mb-3" style={{ color: teal }}>Platform Pillars</div>
          <div className="grid grid-cols-3 gap-3">
            {content.solution_overview.pillars.map((p) => (
              <div key={p.title} className="border border-slate-200 p-3">
                <h3 className="font-head font-semibold text-sm mb-1" style={{ color: teal }}>{p.title}</h3>
                <p className="text-xs text-slate-600">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </Page>

      {/* PROCESS FLOWS */}
      {content.process_flows.map((f, idx) => (
        <Page key={f.key} id={`sec-flow-${idx}`} style={{ minHeight: 1123 }}>
          <div className={pad}>
            <SectionHead num={String(idx + 5).padStart(2, "0")} title={`Process Flow — ${f.title}`} primary={teal} navy={navy} />
            <div className="p-3 mb-3 text-sm text-slate-700 bg-[#F4F5F7]" style={{ borderLeft: `3px solid ${navy}` }}>{f.summary}</div>
            {diagrams[f.key] ? (
              <img src={diagrams[f.key]} alt={f.title} className="w-full border border-slate-200 rounded-sm my-4" />
            ) : (
              <div className="border border-slate-200 rounded-sm my-4 p-6">
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <div className="text-white text-center rounded-sm px-4 py-3 text-xs" style={{ background: navy, minWidth: 130 }}>
                    Main Consignment Bin
                    <div className="text-[10px] opacity-70 mt-0.5">Gauteng · acts as a region</div>
                  </div>
                  <div className="text-2xl font-bold" style={{ color: teal }}>→</div>
                  <div className="text-white text-center rounded-sm px-4 py-3 text-xs" style={{ background: teal }}>Regional Warehouse</div>
                  <div className="text-2xl font-bold" style={{ color: teal }}>→</div>
                  <div className="flex flex-col gap-2">
                    <div className="text-center rounded-sm px-4 py-2 text-xs border" style={{ borderColor: navy, color: navy }}>Fitment Centres</div>
                    <div className="text-center rounded-sm px-4 py-2 text-xs border" style={{ borderColor: navy, color: navy }}>In-house Technicians</div>
                  </div>
                </div>
                <div className="text-center text-[11px] text-slate-500 mt-4">System-generated transfer mail + distribution report · No AX visibility · Driven by consolidated regional demand</div>
              </div>
            )}
            <div className="overline mb-3" style={{ color: teal }}>Step Sequence</div>
            <div className="space-y-3">
              {f.steps.map((s, i) => (
                <div key={i} className="flex gap-3">
                  <div className="shrink-0 h-7 w-7 rounded-full text-white text-xs font-bold flex items-center justify-center font-head" style={{ background: teal }}>{i + 1}</div>
                  <div>
                    <h3 className="font-head font-semibold text-sm" style={{ color: navy }}>{s.title}</h3>
                    <p className="text-sm text-slate-600">{s.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Page>
      ))}

      {/* ROLES */}
      <Page id="sec-roles" style={{ minHeight: 1123 }}>
        <div className={pad}>
          <SectionHead num="11" title="Roles & Permissions Matrix" primary={teal} navy={navy} />
          <p className="text-[15px] text-slate-700 mb-4">Access is granted on a least-privilege basis. The matrix below maps each platform capability to the six operating roles.</p>
          <table className="w-full text-xs border-collapse" data-testid="roles-table">
            <thead>
              <tr>
                <th className="text-left p-2.5 text-white font-head" style={{ background: navy }}>Capability</th>
                {content.roles.map((r) => (
                  <th key={r} className="text-left p-2.5 text-white font-head text-[11px]" style={{ background: navy }}>{r}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {content.permissions.map((row, i) => (
                <tr key={row.feature} className={i % 2 ? "bg-slate-50" : ""}>
                  <td className="p-2.5 border-b border-slate-200 font-semibold" style={{ color: navy }}>{row.feature}</td>
                  {row.access.map((a, j) => (
                    <td key={j} className="p-2.5 border-b border-slate-200 font-medium"
                      style={{ color: a === "Full" ? teal : a === "—" ? "#cbd5e1" : "#374151" }}>{a}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Page>

      {/* INTEGRATION */}
      <Page id="sec-integration" style={{ minHeight: 1123 }}>
        <div className={pad}>
          <SectionHead num="12" title="Integration Summary" primary={teal} navy={navy} />
          <p className="text-[15px] text-slate-700 mb-4">{content.integration_narrative}</p>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>{["Point", "Event", "Trigger", "Flow", "Email Content"].map((h) => (
                <th key={h} className="text-left p-2.5 text-white font-head" style={{ background: navy }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {content.integration.map((i, idx) => (
                <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}>
                  <td className="p-2.5 border-b border-slate-200 font-semibold align-top" style={{ color: navy }}>{i.point}</td>
                  <td className="p-2.5 border-b border-slate-200 align-top"><span className="px-2 py-0.5 rounded-sm text-[10px] font-bold" style={{ background: `${teal}22`, color: teal }}>{i.event}</span></td>
                  <td className="p-2.5 border-b border-slate-200 text-slate-600 align-top">{i.trigger}</td>
                  <td className="p-2.5 border-b border-slate-200 font-semibold align-top" style={{ color: navy }}>{i.flow}</td>
                  <td className="p-2.5 border-b border-slate-200 text-slate-600 align-top">{i.content}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Page>

      {/* BUSINESS RULES & INTEGRATION DATA */}
      <Page id="sec-rules" style={{ minHeight: 1123 }}>
        <div className={pad}>
          <SectionHead num="12" title="Business Rules & Integration Data" primary={teal} navy={navy} />
          <div className="overline mb-3" style={{ color: teal }}>Business Rule Findings</div>
          {content.business_rules.map((r) => (
            <div key={r.title} className="pl-4 mb-4" style={{ borderLeft: `3px solid ${teal}` }}>
              <h3 className="font-head font-semibold text-[15px]" style={{ color: navy }}>{r.title}</h3>
              <p className="text-sm text-slate-600">{r.detail}</p>
            </div>
          ))}
          {content.integration_data.map((grp) => (
            <div key={grp.point} className="mt-7">
              <div className="overline mb-2" style={{ color: teal }}>{grp.point} — Data Received · {grp.event}</div>
              <div className="p-3 mb-3 text-sm text-slate-700 bg-[#F4F5F7]" style={{ borderLeft: `3px solid ${navy}` }}>
                {grp.description} <span className="font-semibold" style={{ color: navy }}>({grp.source})</span>
              </div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr>{["Field", "Description", "Example"].map((h) => (
                    <th key={h} className="text-left p-2.5 text-white font-head" style={{ background: navy }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {grp.fields.map((f, idx) => (
                    <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}>
                      <td className="p-2.5 border-b border-slate-200 font-semibold align-top" style={{ color: navy }}>{f.field}</td>
                      <td className="p-2.5 border-b border-slate-200 text-slate-600 align-top">{f.description}</td>
                      <td className="p-2.5 border-b border-slate-200 text-slate-500 align-top font-label">{f.example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <div className="mt-7">
            <div className="overline mb-2" style={{ color: teal }}>Work Order — Installer Capture (on our system)</div>
            <div className="p-3 mb-3 text-sm text-slate-700 bg-[#F4F5F7]" style={{ borderLeft: `3px solid ${navy}` }}>
              {content.installer_capture.narrative}
            </div>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>{["Field", "Description", "Example", "Source"].map((h) => (
                  <th key={h} className="text-left p-2.5 text-white font-head" style={{ background: navy }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {content.installer_capture.fields.map((f, idx) => (
                  <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}>
                    <td className="p-2.5 border-b border-slate-200 font-semibold align-top" style={{ color: navy }}>{f.field}</td>
                    <td className="p-2.5 border-b border-slate-200 text-slate-600 align-top">{f.description}</td>
                    <td className="p-2.5 border-b border-slate-200 text-slate-500 align-top font-label">{f.example}</td>
                    <td className="p-2.5 border-b border-slate-200 align-top"><span className="px-2 py-0.5 rounded-sm text-[10px] font-bold" style={{ background: `${teal}22`, color: teal }}>{f.source}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-7">
            <div className="overline mb-2" style={{ color: teal }}>Work Order Use Cases — Process Variances</div>
            <p className="text-sm text-slate-700 mb-3">{content.wo_variance_narrative}</p>
            <table className="w-full text-[11px] border-collapse">
              <thead>
                <tr>{["Job Type", "Consignment Stock Effect", "IP2 Activation", "Returns", "Installer Captures", "Notes"].map((h) => (
                  <th key={h} className="text-left p-2 text-white font-head text-[10px] align-top" style={{ background: navy }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {content.wo_variances.map((v, idx) => (
                  <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}>
                    <td className="p-2 border-b border-slate-200 font-semibold align-top" style={{ color: navy }}>{v.job_type}</td>
                    <td className="p-2 border-b border-slate-200 text-slate-600 align-top">{v.stock_effect}</td>
                    <td className="p-2 border-b border-slate-200 text-slate-600 align-top">{v.ip2}</td>
                    <td className="p-2 border-b border-slate-200 text-slate-600 align-top">{v.returns}</td>
                    <td className="p-2 border-b border-slate-200 text-slate-600 align-top">{v.capture}</td>
                    <td className="p-2 border-b border-slate-200 text-slate-500 align-top">{v.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Page>
      <Page id="sec-kpis" style={{ minHeight: 1123 }}>
        <div className={pad}>
          <SectionHead num="14" title="Key Performance Indicators" primary={teal} navy={navy} />
          <p className="text-[15px] text-slate-700 mb-4">Pilot success will be measured against the following operational and financial targets.</p>
          <div className="grid grid-cols-3 gap-3">
            {content.kpis.map((k) => (
              <div key={k.label} className="border border-slate-200 p-3" style={{ borderTop: `3px solid ${teal}` }}>
                <div className="overline text-slate-500">{k.label}</div>
                <div className="font-head text-2xl font-bold my-1" style={{ color: navy }}>{k.target}</div>
                <div className="text-xs text-slate-600">{k.description}</div>
              </div>
            ))}
          </div>
        </div>
      </Page>

      {/* OPEN ITEMS */}
      <Page id="sec-open" style={{ minHeight: 1123 }}>
        <div className={pad}>
          <SectionHead num="14" title="Open Information Requirements" primary={teal} navy={navy} />
          <p className="text-[15px] text-slate-700 mb-4">The following items are outstanding. The build proceeds with placeholders; each can be updated and the document re-exported instantly.</p>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>{["Item", "Owner", "Status"].map((h) => (
                <th key={h} className="text-left p-2.5 text-white font-head" style={{ background: navy }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {content.open_items.map((it, idx) => (
                <tr key={idx} className={idx % 2 ? "bg-slate-50" : ""}>
                  <td className="p-2.5 border-b border-slate-200 font-semibold" style={{ color: navy }}>{it.item}</td>
                  <td className="p-2.5 border-b border-slate-200 text-slate-600">{it.owner}</td>
                  <td className="p-2.5 border-b border-slate-200"><span className="px-2 py-0.5 rounded-sm text-[10px] font-bold" style={{ background: `${teal}22`, color: teal }}>{it.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Page>
    </div>
  );
};
